/**
 * UserAvatar — the signed-in user's header avatar.
 *
 * Shows their profile photo when one is set, otherwise falls back to initials
 * (the previous behaviour). Used by every header (Admin / HR / Member) and the
 * mobile drawer so all four stay in sync.
 *
 * Photo source: `userdata.profile_photo` in localStorage renders immediately
 * (no flicker on navigation), and `refreshCurrentUser()` re-fetches
 * GET /api/auth/me once per page load so a photo changed elsewhere in the app
 * appears without needing to log out and back in.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_URL, UPLOAD_URL } from "../api/api";

const readUserData = () => {
  try {
    return JSON.parse(localStorage.getItem("userdata") || "{}");
  } catch {
    return {};
  }
};

// Stored paths look like "/uploads/team/xyz.jpg"; absolute URLs pass through.
export const photoUrl = (p) =>
  !p ? null : /^https?:\/\//.test(p) ? p : `${UPLOAD_URL}${p.startsWith("/") ? "" : "/"}${p}`;

// Re-fetch the signed-in user and merge into localStorage.userdata. Notifies
// listeners via a `userdataChanged` event so every mounted avatar updates.
// Best-effort: a failure just leaves the cached value in place.
let inFlight = null;
export const refreshCurrentUser = () => {
  const token = localStorage.getItem("token");
  if (!token || inFlight) return inFlight || Promise.resolve(null);
  inFlight = axios
    .get(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
    .then((res) => {
      const fresh = res?.data?.user;
      if (!fresh) return null;
      const merged = { ...readUserData(), ...fresh };
      localStorage.setItem("userdata", JSON.stringify(merged));
      window.dispatchEvent(new Event("userdataChanged"));
      return merged;
    })
    .catch(() => null)
    .finally(() => { inFlight = null; });
  return inFlight;
};

export default function UserAvatar({ initials = "", className = "usrimg", refresh = false }) {
  const [photo, setPhoto] = useState(() => readUserData().profile_photo || null);
  // An image that 404s (file deleted on disk) must not leave a broken icon —
  // fall back to initials instead.
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    const sync = () => {
      const next = readUserData().profile_photo || null;
      setPhoto((prev) => (prev === next ? prev : (setBroken(false), next)));
    };
    window.addEventListener("userdataChanged", sync);
    window.addEventListener("storage", sync);
    if (refresh) refreshCurrentUser();
    return () => {
      window.removeEventListener("userdataChanged", sync);
      window.removeEventListener("storage", sync);
    };
  }, [refresh]);

  const src = broken ? null : photoUrl(photo);

  return (
    <div className={className}>
      {src ? (
        <img
          src={src}
          alt=""
          onError={() => setBroken(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit", display: "block" }}
        />
      ) : (
        initials
      )}
    </div>
  );
}
